import { useMemo } from 'react'

import {
  Line,
  LineChart as RCLineChart,
  Tooltip as RCTooltip,
  ResponsiveContainer,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'

import styles from './Chart.module.scss'
import { Tick, Tooltip } from './components'

interface RenderProps<T> {
  index: number
  payload: { value: T }
  x: number
  y: number
}

export interface DataEntry<X> {
  key: X
  values: number[]
}

interface LineChartProps<X> {
  data: DataEntry<X>[]
  formatTooltipLabel: (value: string) => string
  formatTooltipValue?: (value: number, index: number) => JSX.Element | string
  formatXTick: (value: X) => string
  formatYTick: (value: number) => string
  reversed?: boolean
  strokeColors?: string[]
  strokeWidth: number
  yScaleFactor?: number
}

const getMaxValue = (values: number[]) =>
  values.reduce((acc, current) => (current > acc ? current : acc), 0)

const LineChart = <X,>({
  data,
  formatTooltipLabel,
  formatTooltipValue,
  formatXTick,
  formatYTick,
  reversed,
  strokeColors = ['#9DD2B0'],
  strokeWidth,
  yScaleFactor = 1,
}: LineChartProps<X>) => {
  const maxValue = data.reduce((acc, current) => {
    const maxValue = getMaxValue(current.values)
    return maxValue > acc ? maxValue : acc
  }, 0)

  const chartData = useMemo(
    () =>
      (reversed ? data.reverse() : data).map((entry) => {
        const values: Record<string, number> = {}
        entry.values.forEach((value, index) => (values[`value${index}`] = value))

        return {
          key: entry.key,
          ...values,
        }
      }),
    [data, reversed],
  )

  return (
    <ResponsiveContainer width="100%" height={292}>
      <RCLineChart
        className={styles.chart}
        data={chartData}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
      >
        <XAxis
          className={styles.xAxis}
          dataKey="key"
          interval={0}
          tick={({ index, payload, x, y }: RenderProps<string>) =>
            index === 0 || index === chartData.length - 1 ? (
              <Tick
                dy={'0.71em'}
                textAnchor={index === 0 ? 'start' : 'end'}
                value={formatXTick(payload.value as X)}
                x={x}
                y={y + 5}
              />
            ) : (
              <></>
            )
          }
          tickLine={false}
        />
        <YAxis
          className={styles.yAxis}
          domain={[0, yScaleFactor * maxValue]}
          width={30}
          tick={({ index, payload, x, y }: RenderProps<number>) =>
            index === 0 ? (
              <Tick
                dy={'0.355em'}
                textAnchor={'end'}
                value={formatYTick(payload.value)}
                x={x}
                y={y}
              />
            ) : (
              <></>
            )
          }
          tickLine={false}
        />
        <RCTooltip
          cursor={{
            stroke: '#7D7D7D',
            strokeWidth: 1,
            strokeDasharray: '2 2',
          }}
          labelClassName={styles.label}
          wrapperClassName={styles.tooltip}
          itemStyle={{ background: 'red' }}
          content={({ active, label, payload }: TooltipProps<number, number>) =>
            active && label ? (
              <Tooltip
                formatValue={formatTooltipValue ?? formatYTick}
                label={formatTooltipLabel(label as string)}
                values={payload?.map((value) => value.value!) ?? []}
              />
            ) : null
          }
        />
        {data.length > 0 &&
          strokeColors.map((strokeColor, index) => (
            <Line
              activeDot={{
                fill: 'white',
                r: 5,
                stroke: strokeColor,
                strokeWidth,
              }}
              dataKey={`value${index}`}
              key={index}
              stroke={strokeColor}
              strokeLinejoin="round"
              strokeWidth={strokeWidth}
              dot={false}
              type="linear"
            />
          ))}
      </RCLineChart>
    </ResponsiveContainer>
  )
}

export default LineChart
